import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAssessmentResultsDetailed } from "../../../../services/userService";
import { Icon } from "@iconify/react";

function Result() {
  const location = useLocation();
  const navigate = useNavigate();
  const assessmentId = location.state?.assessmentId;
  const moduleName = location.state?.moduleName;
  const lessonName = location.state?.lessonName;
  const [assessmentResults, setAssessmentResults] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  useEffect(() => {
    if (assessmentId) fetchResults();
  }, [assessmentId]);

  async function fetchResults() {
    try {
      const response = await getAssessmentResultsDetailed(assessmentId);
      // console.log("response", response);
      setAssessmentResults(response.data);
    } catch (error) {
      // console.log("error", error);
    }
  }

  const handlePrevQuestion = () => {
    setCurrentQuestionIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextQuestion = () => {
    if (assessmentResults) {
      setCurrentQuestionIndex((prev) =>
        Math.min(assessmentResults.questions.length - 1, prev + 1)
      );
    }
  };

  const handlePlayVideo = (videoId, timestamp, videoUrl) => {
    navigate("/user/resultvideo", {
      state: {
        videoId,
        timestamp,
        videoUrl,
      },
    });
  };

  const renderMediaContent = (type, url, content, showContentValue = false) => (
    <div className="my-3 text-center">
      {showContentValue && <div className="mb-2 fw-semibold">{content}</div>}
      {type === "image" && (
        <img
          src={url}
          alt={content}
          className="img-fluid rounded"
          style={{
            maxWidth: "300px",
            maxHeight: "200px",
            objectFit: "contain",
          }}
        />
      )}
      {type === "audio" && (
        <audio
          controls
          src={url}
          className="w-100"
          style={{ maxWidth: "300px" }}
        >
          Your browser does not support audio.
        </audio>
      )}
    </div>
  );

  if (!assessmentResults || !assessmentId) {
    return <div className="text-center py-5 text-lg text-gray-500">Loading...</div>;
  }

  const currentQuestion = assessmentResults.questions[currentQuestionIndex];
  if (!currentQuestion) {
    return (
      <div className="text-center py-5 text-lg text-gray-500">
        Question data not available
      </div>
    );
  }

  const userAnswers = currentQuestion.user_answer
    ? JSON.parse(currentQuestion.user_answer)
    : [];
  const correctOptions = currentQuestion.correct_option || [];

  return (
    <div className="container p-0 md:py-4">
      <div className="bg-white shadow-sm rounded-xl border border-gray-200">
        {/* Header with Back Button */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <Icon icon="fluent:arrow-left-24-regular" className="w-5 h-5 text-gray-700" />
          </button>
          <div className="min-w-0">
            <h4 className="font-semibold text-lg text-font-primary">Assessment Results</h4>
            {(moduleName || lessonName) && (
              <p className="text-xs text-gray-500 mt-0.5">
                {moduleName ? `Module: ${moduleName}` : ''}{moduleName && lessonName ? ' · ' : ''}{lessonName ? `Lesson: ${lessonName}` : ''}
              </p>
            )}
          </div>
        </div>

        {/* Question Header */}
        <div className="px-5 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h5 className="font-semibold text-font-primary mb-1">
                Question {currentQuestionIndex + 1} of {assessmentResults.questions.length}
              </h5>
              {currentQuestion.video_timestamp && (
                <div className="text-font-secondary text-sm">
                  Timestamp: {currentQuestion.video_timestamp}
                </div>
              )}
            </div>

            {currentQuestion.ref_video_id && currentQuestion.video_timestamp && (
              <button
                className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-xl text-sm flex items-center gap-2 transition-colors"
                onClick={() =>
                  handlePlayVideo(
                    currentQuestion.ref_video_id,
                    currentQuestion.video_timestamp,
                    currentQuestion.video_url
                  )
                }
              >
                <Icon icon="fluent:play-circle-24-regular" className="w-4 h-4" />
                Play Video
              </button>
            )}
          </div>
        </div>

        {/* Question Text */}
        <div className="px-5 py-4">
          <p className="font-medium text-font-primary mb-3">{currentQuestion.question}</p>
          {(currentQuestion.question_content_type === "image" ||
            currentQuestion.question_content_type === "audio") &&
            renderMediaContent(
              currentQuestion.question_content_type,
              currentQuestion.question_media_url,
              currentQuestion.question
            )}
        </div>

        {/* Options List */}
        <div className="px-5 py-4 border-t border-gray-200">
          <h6 className="text-sm font-medium text-font-secondary mb-3">Answers</h6>
          <div className="flex flex-col gap-3">
            {currentQuestion.options?.map((option) => {
              const isUserSelected = userAnswers.includes(option.id.toString());
              const isCorrectAnswer = correctOptions.includes(option.content_value);

              return (
                <div
                  key={option.id}
                  className={`border rounded-xl p-4 ${
                    isUserSelected || isCorrectAnswer ? "bg-gray-50" : "bg-white"
                  } ${isCorrectAnswer ? "border-green-300" : "border-gray-200"}`}
                >
                  {option.content_type === "text" ? (
                    <p className="text-font-primary mb-2">{option.content_value}</p>
                  ) : (
                    renderMediaContent(
                      option.content_type,
                      option.media_url,
                      option.content_value,
                      true
                    )
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {isUserSelected && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700">
                        <Icon icon="fluent:checkmark-circle-24-regular" className="w-3.5 h-3.5" />
                        Your Choice
                      </span>
                    )}
                    {isCorrectAnswer && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-green-50 text-green-700">
                        <Icon icon="fluent:checkmark-circle-24-filled" className="w-3.5 h-3.5" />
                        Correct Answer
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Feedback */}
        <div className="px-5 py-4 border-t border-gray-200">
          {currentQuestion.user_is_correct ? (
            <div className="flex justify-center items-center gap-2 text-green-600">
              <Icon icon="fluent:checkmark-circle-24-filled" className="w-5 h-5" />
              <span className="font-semibold">Correct Answer</span>
            </div>
          ) : (
            <div className="flex justify-center items-center gap-2 text-red-600">
              <Icon icon="fluent:dismiss-circle-24-filled" className="w-5 h-5" />
              <span className="font-semibold">Incorrect Answer</span>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={handlePrevQuestion}
              disabled={currentQuestionIndex === 0}
              className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-font-primary font-medium py-2.5 px-5 rounded-xl border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
            >
              <Icon icon="fluent:chevron-left-24-regular" className="w-4 h-4" />
              Previous
            </button>
            <button
              onClick={handleNextQuestion}
              disabled={currentQuestionIndex === assessmentResults.questions.length - 1}
              className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-medium py-2.5 px-5 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
            >
              Next
              <Icon icon="fluent:chevron-right-24-regular" className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Result;
